/**
 * Dokumentene paa disk.
 *
 * OPFS, samme sted som loggen. Det er ikke tilfeldig: `docs/02-arkitektur.md`
 * og CLAUDE.md krever at «dokumenter forlater aldri appen» — ingen
 * delingsknapp, ingen «aapne i …», og en lokal kopi som faktisk kan slettes.
 * OPFS er utilgjengelig for andre nettsteder og usynlig i Filer, og filene
 * kan fjernes av oss naar de skal bort.
 *
 * FILNAVNET BAERER VERSJONEN: `<serieId>-v<n>.pdf`. Da er «har jeg versjon 3»
 * et spoersmaal om en fil finnes, og en gammel versjon er en fil med et annet
 * navn — som kan slettes uten at vi foerst maa lese noe.
 *
 * Alt her svelger feil og returnerer null eller false. En telefon kan nekte
 * lagring, og da skal appen hente over nett som foer, ikke stanse.
 */

const MAPPE = "dokumenter";

async function mappe(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const rot = await navigator.storage.getDirectory();
    return await rot.getDirectoryHandle(MAPPE, { create: true });
  } catch {
    return null;
  }
}

export function filnavnFor(serieId: string, versjon: number): string {
  return `${serieId}-v${versjon}.pdf`;
}

/**
 * Skriver fila. Returnerer false naar den ikke kom ned.
 *
 * ARRAYBUFFER OG IKKE BLOB. `write(blob)` er lovlig etter spesifikasjonen,
 * men paa iOS endte den med en fil som fanns og var TOM — og en tom fil er
 * verre enn ingen fil, fordi den ser ut som en vellykket lagring hele veien
 * fram til mottakeren sier «No content provided».
 *
 * DERFOR LESES STOERRELSEN TILBAKE. Det er den eneste maaten aa vite at
 * noe faktisk ble skrevet: hverken `write` eller `close` kastet.
 */
export async function lagre(navn: string, data: Blob): Promise<boolean> {
  const m = await mappe();
  if (m === null) return false;
  try {
    const fil = await m.getFileHandle(navn, { create: true });
    const strøm = await fil.createWritable();
    await strøm.write(await data.arrayBuffer());
    await strøm.close();

    const skrevet = (await fil.getFile()).size;
    if (skrevet === data.size) return true;

    /* Fila lyver. Da skal den ikke bli liggende og se gyldig ut. */
    await m.removeEntry(navn).catch(() => { /* alt borte */ });
    return false;
  } catch {
    return false;
  }
}

/**
 * Leser fila og gir en URL appen kan vise.
 *
 * Blob-URL og ikke en filsti: den lever i denne fana, kan ikke deles videre,
 * og slippes med `URL.revokeObjectURL` naar flata rives. Samme krav som
 * gjelder visningen ellers.
 */
export async function les(navn: string): Promise<string | null> {
  const m = await mappe();
  if (m === null) return null;
  try {
    const fil = await m.getFileHandle(navn);
    return URL.createObjectURL(await fil.getFile());
  } catch {
    // Finnes ikke. Det er en normal tilstand, ikke en feil.
    return null;
  }
}

/**
 * Fila selv, ikke en URL.
 *
 * Trengs naar noe skal SENDES videre — en rapport som venter paa dekning.
 * `les` gir en blob-URL til visning; den kan ikke lastes opp.
 */
export async function lesFil(navn: string): Promise<File | null> {
  const m = await mappe();
  if (m === null) return null;
  try {
    const fil = await m.getFileHandle(navn);
    const f = await fil.getFile();
    /* En tom fil er ingen fil. Lastes den opp, svarer tjeneren «No content
       provided», og den som ventet paa rapporten fikk ingenting. */
    return f.size === 0 ? null : f;
  } catch {
    return null;
  }
}

export async function slett(navn: string): Promise<void> {
  const m = await mappe();
  if (m === null) return;
  try {
    await m.removeEntry(navn);
  } catch {
    // Alt borte, eller aldri skrevet.
  }
}

/**
 * Sletter referansebildene til én produksjon.
 *
 * De ligger i samme mappe som dokumentene og foelger samme regel: alt skal
 * bort naar produksjonen er over. Navnet baerer produksjonen — se
 * `state/shotlist.ts` — saa det er prefikset som avgjoer, og ingen andre
 * produksjoners bilder roeres.
 */
export async function slettReferansebilder(produksjonId: string): Promise<void> {
  const m = await mappe();
  if (m === null) return;
  try {
    const navn: string[] = [];
    // @ts-expect-error `keys()` finnes paa OPFS-mapper, men ikke i lib.dom ennaa.
    for await (const n of m.keys()) navn.push(n as string);
    const prefiks = `ref-${produksjonId}_`;
    for (const n of navn) {
      if (n.startsWith(prefiks)) await m.removeEntry(n);
    }
  } catch {
    // Lagringen er utilgjengelig. Neste forsoek tar dem.
  }
}

/**
 * Sletter filene for en liste serier, uansett versjon.
 *
 * Brukes naar produksjonen passerer sluttdatoen. Vi kjenner ikke
 * versjonsnumrene paa det tidspunktet — radene er alt borte — saa her
 * gaas mappa gjennom og alt som begynner med serie-id-en fjernes.
 */
export async function slettForSerier(serieIder: readonly string[]): Promise<void> {
  if (serieIder.length === 0) return;
  const m = await mappe();
  if (m === null) return;
  try {
    const navn: string[] = [];
    // @ts-expect-error `keys()` finnes paa OPFS-mapper, men ikke i lib.dom ennaa.
    for await (const n of m.keys()) navn.push(n as string);
    for (const n of navn) {
      if (serieIder.some((id) => n.startsWith(`${id}-`))) await m.removeEntry(n);
    }
  } catch {
    // Ingenting aa gjoere. Radene er uansett borte.
  }
}
